import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowDown, FiArrowUpRight, FiPlay } from 'react-icons/fi';
import useScrollScene, { clamp, smooth } from './useScrollScene';
import './voices-journeys.css';
import { HomeFormMeaning } from './VoicesHomeMeaning';
import { projectCraftLabels, projectHomeLabels } from './craftLabels';
import { useVoicesTheme } from './VoicesTheme';

const chapters = [
  { label: 'Scope the work', title: 'One workflow.', emphasis: 'A clear purpose.', description: 'Start with one job, the knowledge it needs, and a person accountable for the result.', link: '/ai-agents', cta: 'See custom AI agents' },
  { label: 'Build the system', title: 'Knowledge. Actions.', emphasis: 'Controls.', description: 'Connect approved knowledge and actions. Define checks and handoffs before the agent goes to work.', link: '/services/salesforce-agentforce', cta: 'Explore the platform' },
  { label: 'Review and improve', title: 'Keep people', emphasis: 'in the loop.', description: 'Evaluate results, review exceptions, and improve the workflow with your team.', link: '/managed-agents', cta: 'Explore ongoing care' },
];

function visibility(element, opacity) {
  if (!element) return;
  if (opacity < .1 && element.contains(document.activeElement)) {
    element.closest('.vj-journey')?.querySelector('[data-journey-focus="true"]')?.focus({ preventScroll: true });
  }
  element.style.opacity = opacity;
  element.style.visibility = opacity > .01 ? 'visible' : 'hidden';
  element.inert = opacity < .1;
  element.setAttribute('aria-hidden', opacity < .1 ? 'true' : 'false');
}

function Scene({ scene, poster = false }) {
  const { theme } = useVoicesTheme();
  return <div className="vj-scene" data-renderer={scene.status} aria-hidden="true">
    {poster && <img className="vj-poster" src={theme === 'light' ? '/assets/voices/reference-focus-transparent.png' : '/assets/voices/reference-focus-2x.png'} alt="" width="920" height="870" />}
    <canvas ref={scene.canvas} data-scene="true" />
  </div>;
}

export function HeroJourney({ reduced, toggleMotion, featured }) {
  const copies = useRef([]);
  const bridge = useRef(null);
  const proofs = useRef([]);
  const photos = useRef([]);
  const frames = useRef([]);
  const title = useRef(null);
  const baseline = useRef(null);
  const cue = useRef(null);
  const progress = useRef(null);
  const proofCopies = useRef([]);
  const proofNotes = useRef([]);
  const labels = useRef([]);
  const scene = useScrollScene({ section: 'hero', reduced, onFrame: (t, snapshot) => {
    const { width, height, portal } = snapshot;
    const h = clamp(t / .52);
    const projected = projectHomeLabels(h, snapshot);
    labels.current.forEach((element, i) => {
      const label = projected[i];
      element.hidden = !label;
      if (label) {
        element.textContent = label.text;
        element.style.left = `${label.x}px`;
        element.style.top = `${label.y}px`;
        element.style.opacity = label.opacity;
      }
    });
    const mobile = width < 800;
    const ranges = mobile ? [[0, 0, .10, .18], [.255, .30, .42, .49], [.50, .56, .73, .79], [.80, .86, 1, 2]] : [[0, 0, .10, .18], [.30, .34, .44, .50], [.50, .57, .74, .80], [.80, .88, 1, 2]];
    ranges.forEach(([a, b, c, d], i) => {
      const opacity = (i === 0 ? 1 : smooth(a, b, h)) * (1 - smooth(c, d, h));
      visibility(copies.current[i], opacity * (1 - smooth(.49, .54, t)));
      if (i && copies.current[i]) copies.current[i].style.transform = `translateY(${mobile ? 0 : -43}%) translateY(${(1 - opacity) * (h < b ? 18 : -18)}px)`;
    });
    if (title.current) title.current.style.transform = `translateY(${-smooth(.10, .28, h) * 22}px) scale(${1 + smooth(.10, .28, h) * .07})`;
    visibility(baseline.current, 1 - smooth(.06, .17, h));
    if (cue.current) cue.current.style.opacity = 1 - smooth(.03, .13, h);
    if (progress.current) progress.current.style.transform = `scaleX(${h})`;
    if (scene.stage.current) {
      scene.stage.current.dataset.chapter = t >= .53 ? 'evidence' : h < .26 ? '0' : h < .56 ? '1' : h < .82 ? '2' : '3';
      scene.stage.current.style.setProperty('--vj-controls-alpha', 1 - smooth(.03, .13, h));
    }
    visibility(bridge.current, smooth(.52, .55, t) * (1 - smooth(.605, .646, t)));
    const expand = smooth(.635, .688, t);
    const frame = portal || { x: width * .5, y: height * .5, rx: width * .13, ry: height * .2 };
    const mix = (a, b, p) => a + (b - a) * p;
    const box = { x: mix(frame.x, width / 2, expand), y: mix(frame.y, height / 2, expand), rx: mix(frame.rx, width / 2 + 1, expand), ry: mix(frame.ry, height / 2 + 1, expand) };
    const inset = [box.y - box.ry, width - box.x - box.rx, height - box.y - box.ry, box.x - box.rx].map(value => Math.max(0, value));
    if (photos.current[0]) photos.current[0].style.clipPath = `inset(${inset.map(n => `${n}px`).join(' ')} round ${Math.min(frame.rx, frame.ry) * (1 - expand)}px)`;
    visibility(proofs.current[0], smooth(.552, .583, t));
    visibility(proofCopies.current[0], smooth(.67, .713, t) * (1 - smooth(.784, .812, t)));
    visibility(proofNotes.current[0], smooth(.67, .713, t) * (1 - smooth(.784, .812, t)));
    const radius = mix(2, Math.max(width, height) * 1.46, smooth(.80, .89, t));
    const corner = radius * (1 - smooth(.867, .895, t));
    const secondInset = [height * .45 - radius, width * .5 - radius, height * .55 - radius, width * .5 - radius].map(value => Math.max(0, value));
    if (photos.current[1]) photos.current[1].style.clipPath = `inset(${secondInset.map(n => `${n}px`).join(' ')} round ${corner}px)`;
    if (frames.current[1]) Object.assign(frames.current[1].style, { left: `${width * .5 - radius}px`, top: `${height * .45 - radius}px`, width: `${radius * 2}px`, height: `${radius * 2}px`, borderRadius: `${corner}px`, opacity: smooth(.80, .816, t) * (1 - smooth(.876, .898, t)) });
    visibility(proofs.current[1], smooth(.801, .82, t));
    visibility(proofCopies.current[1], smooth(.876, .917, t));
    visibility(proofNotes.current[1], smooth(.876, .917, t));
    if (scene.canvas.current) scene.canvas.current.style.opacity = 1 - smooth(.668, .691, t);
    if (proofs.current[1]) proofs.current[1].style.setProperty('--vj-exit', smooth(.963, 1, t));
  } });
  useLayoutEffect(() => {
    copies.current.forEach((element, i) => visibility(element, !scene.active || i === 0 ? 1 : 0));
    [...proofs.current, ...proofCopies.current, ...proofNotes.current].forEach(element => visibility(element, scene.active ? 0 : 1));
    visibility(bridge.current, 0);
    if (scene.canvas.current) scene.canvas.current.style.opacity = 1;
    visibility(baseline.current, 1);
    labels.current.forEach(element => { if (element) element.hidden = true; });
    if (!scene.active && title.current) title.current.style.transform = 'none';
  }, [scene.active, scene.canvas]);

  return <section ref={scene.root} className="vj-journey vj-hero" data-motion={scene.active ? 'active' : 'calm'} aria-labelledby="v-hero-title">
    <div ref={scene.stage} className="vj-stage">
      <Scene scene={scene} poster />
      <div className="vj-chapter vj-opening" ref={el => { copies.current[0] = el; }}>
        {!scene.active && <img className="vj-calm-poster" src="/assets/voices/reference-focus-2x.png" alt="" width="920" height="870" />}
        <div className="vj-opening-title" ref={title}><p className="v-label">Salesforce. Agentforce. Enterprise AI.</p><h1 id="v-hero-title">Enterprise AI.<br /><em>Made useful.</em></h1></div>
        <div className="vj-baseline" ref={baseline}><p>Custom AI agents. Salesforce &amp; Agentforce.<br />Designed, connected and managed by Antrazal.</p><div><Link className="v-button v-button-light" to="/work">See customer work <FiArrowUpRight aria-hidden="true" /></Link><a className="v-text-link" href="#company-film">Meet Antrazal <FiPlay aria-hidden="true" /></a></div></div>
      </div>
      {chapters.map((chapter, i) => <div className="vj-chapter vj-story-chapter" key={chapter.label} ref={el => { copies.current[i + 1] = el; }}>
        <p className="v-label">0{i + 1} / {chapter.label}</p><h2>{chapter.title}<br /><em>{chapter.emphasis}</em></h2><p className="vj-chapter-description">{chapter.description}</p><Link className="v-text-link" to={chapter.link}>{chapter.cta}<FiArrowUpRight aria-hidden="true" /></Link><HomeFormMeaning meaning={['agent', 'layers', 'care'][i]} />
      </div>)}
      <p className="vj-bridge" ref={bridge}>From a working system.<br /><span>To the people it works for.</span></p>
      <div className="vj-surface-labels" aria-hidden="true">{[0, 1, 2, 3].map(i => <span key={i} hidden ref={el => { labels.current[i] = el; }} />)}</div>
      {featured.map((item, i) => <section className={`vj-proof vj-proof-${i}`} key={item.slug} ref={el => { proofs.current[i] = el; }} aria-labelledby={`v-proof-title-${i}`}>
        <div className="vj-photo" ref={el => { photos.current[i] = el; }}><img src={item.image} srcSet={item.srcSet} sizes="100vw" alt={i === 0 ? 'Hotel team member beside a luggage cart' : 'Caregiver helping a person walk outdoors'} loading="eager" decoding="async" /></div>
        <div className="vj-photo-frame" ref={el => { frames.current[i] = el; }} aria-hidden="true" />
        <div className="vj-proof-copy" ref={el => { proofCopies.current[i] = el; }}><p className="v-label">{i === 0 ? 'The same approach. In the real world.' : 'From hotel partners. To caregivers.'}</p><span className="vj-client">{item.label}</span><h2 id={`v-proof-title-${i}`}>{item.title}<br />{item.emphasis}</h2><p>{item.description}</p><Link to={`/customer-stories/${item.slug}`} className="v-button v-button-light">Go inside the story <FiArrowUpRight aria-hidden="true" /></Link></div>
        <p className="vj-proof-note" ref={el => { proofNotes.current[i] = el; }}>{item.subtitle}</p>
      </section>)}
      <div className="vj-hero-controls"><a ref={cue} href="#customer-stories" data-meaning="focus" onClick={event => { if (scene.active) { event.preventDefault(); scene.seek(.18); } }}>Zoom to focus <FiArrowDown aria-hidden="true" /></a><button type="button" data-journey-focus="true" onClick={toggleMotion} aria-pressed={reduced}>{reduced ? 'Enable motion' : 'Reduce motion'}</button></div>
      <div className="vj-progress" aria-hidden="true"><i ref={progress} /></div>
    </div>
  </section>;
}

export function CraftJourney({ reduced, stages }) {
  const [selected, setSelected] = useState(0);
  const copies = useRef([]);
  const selectedRef = useRef(0);
  const progress = useRef(null);
  const labels = useRef([]);
  const scene = useScrollScene({ section: 'craft', reduced, onFrame: (p, frame) => {
    const projected = projectCraftLabels(p, frame);
    labels.current.forEach((element, i) => {
      const label = projected[i];
      element.hidden = !label;
      if (label) {
        element.textContent = label.text;
        element.style.left = `${label.x}px`;
        element.style.top = `${label.y}px`;
      }
    });
    if (progress.current) progress.current.style.transform = `scaleX(${p})`;
    const next = p < .335 ? 0 : p < .675 ? 1 : 2;
    if (next !== selectedRef.current) { selectedRef.current = next; setSelected(next); }
    [[0, 0, .27, .335], [.335, .40, .61, .67], [.67, .74, 1, 2]].forEach(([a, b, c, d], i) => {
      const opacity = (i === 0 ? 1 : smooth(a, b, p)) * (1 - smooth(c, d, p));
      visibility(copies.current[i], opacity);
      if (copies.current[i]) copies.current[i].style.transform = `translateY(${scene.stage.current?.clientWidth < 800 ? 0 : -38}%) translateY(${(1 - opacity) * (p < b ? 25 : -22)}px)`;
    });
  } });
  const choose = index => {
    selectedRef.current = index;
    setSelected(index);
    if (scene.active) scene.seek([.08, .49, .89][index]);
    else copies.current.forEach((element, i) => visibility(element, i === index ? 1 : 0));
  };
  useEffect(() => {
    if (!scene.active) copies.current.forEach((element, i) => visibility(element, i === selected ? 1 : 0));
  }, [scene.active, selected]);
  return <section ref={scene.root} className="vj-journey vj-craft" id="expertise" data-motion={scene.active ? 'active' : 'calm'} aria-label="From idea to everyday">
    <div ref={scene.stage} className="vj-stage">
      <Scene scene={scene} />
      <div className="vj-craft-heading"><p className="v-label">From idea to everyday</p><Link className="v-text-link" to="/services">All services <FiArrowUpRight aria-hidden="true" /></Link></div>
      <div className="vj-craft-panels" id="v-service-panel" role="tabpanel" aria-labelledby={`v-service-tab-${selected}`}>
        {stages.map((item, i) => <div className="vj-craft-copy" key={item.label} ref={el => { copies.current[i] = el; }} data-selected={selected === i}>
          <p className="v-label">{item.eyebrow}</p><h2>{item.title}<br /><em>{item.emphasis}</em></h2><p className="vj-description">{item.description}</p><Link className="v-text-link" to={item.link}>{item.cta} <FiArrowUpRight aria-hidden="true" /></Link><HomeFormMeaning meaning={['layers', 'connect', 'care'][i]} />
        </div>)}
      </div>
      <div className="v-studio-tabs" role="tablist" aria-label="Explore our services">{stages.map((item, i) => <button key={item.label} type="button" id={`v-service-tab-${i}`} role="tab" data-journey-focus={selected === i ? 'true' : undefined} aria-selected={selected === i} aria-controls="v-service-panel" tabIndex={selected === i ? 0 : -1} onClick={() => choose(i)} onKeyDown={event => {
        const next = event.key === 'ArrowRight' ? (i + 1) % stages.length : event.key === 'ArrowLeft' ? (i + stages.length - 1) % stages.length : event.key === 'Home' ? 0 : event.key === 'End' ? stages.length - 1 : null;
        if (next !== null) { event.preventDefault(); choose(next); document.getElementById(`v-service-tab-${next}`)?.focus(); }
      }}><span>0{i + 1}</span>{item.label}</button>)}</div>
      <span className="vj-craft-scroll">Scroll to explore {'\u00b7'} Build / Connect / Stay <FiArrowDown aria-hidden="true" /></span>
      <div className="vj-surface-labels" aria-hidden="true">{[0, 1, 2, 3].map(i => <span key={i} hidden ref={el => { labels.current[i] = el; }} />)}</div>
      <div className="vj-progress vj-craft-progress" aria-hidden="true"><i ref={progress} /></div>
    </div>
  </section>;
}

export function EndingJourney({ reduced }) {
  const scene = useScrollScene({ section: 'ending', reduced });
  return <section ref={scene.root} className="vj-journey vj-ending" data-motion={scene.active ? 'active' : 'calm'} aria-labelledby="v-ending-title">
    <div ref={scene.stage} className="vj-stage">
      <Scene scene={scene} />
      <div className="vj-ending-copy"><p className="v-label">Your next chapter</p><h2 id="v-ending-title">Let{'\u2019'}s make<br /><em>work better.</em></h2><p>Start with one workflow. Discuss scope, ownership, and whether we are the right fit.</p><Link className="v-button v-button-light" to="/contact">Start a conversation <FiArrowUpRight aria-hidden="true" /></Link><a className="v-ending-email" href="mailto:info@antrazal.com">info@antrazal.com</a><HomeFormMeaning meaning="focus" /></div>
    </div>
  </section>;
}
